import { buildBindable } from '@monofe/di';
import { createBrowserHistory } from '@remix-run/router'; /* Make sure to import the correct version of #history (react-router) */
import type { Listener } from '@remix-run/router/history';
import action from './action';
import type { IHistory } from './interface';
import { bindHistory, injectHistory, unbindHistory } from './interface';
import { TYPES } from './private';
import tracking from './tracking';

const isRunningOnNodeJS = typeof window === 'undefined';

type BrowserHistory = ReturnType<typeof createBrowserHistory>;

/* Both #BrowserHistory & './tracking' listen to history events, the built-in #BrowserHistory only allows for a single callback
   Replace #listen function for multiple callback support */
function _addMultipleCallbackSupport(history: BrowserHistory): BrowserHistory {
    const callbacks: Listener[] = [];

    history.listen(update => {
        callbacks.forEach(callback => {
            callback(update);
        });
    });

    return Object.freeze({
        ...history,
        listen: (listener: Listener) => {
            callbacks.push(listener);
        },
    }) as BrowserHistory;
}

function _build(): Promise<Readonly<IHistory>> {
    const history = isRunningOnNodeJS
        ? undefined
        : _addMultipleCallbackSupport(createBrowserHistory({ window, v5Compat: true }));

    return Promise.resolve(
        Object.freeze({
            ...history,
            ...action(history),
            ...tracking(history),
            browserHistory: () => history,
        }),
    );
}

export default buildBindable<IHistory>({
    id: TYPES.IHistory,
    bind: bindHistory,
    remove: unbindHistory,
    inject: injectHistory,
    build: _build,
});
